'use client';

import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import {
  Paper,
  Tab,
  Tabs,
  Chip,
  Typography,
  Box,
  Divider,
  ButtonBase,
} from '@mui/material';
import {
  ClubLogo,
  CompetitionRanking,
  CompetitionRankingRow,
  EventItem,
} from '@scorenco/components';
import { RouteLink, Types } from '@scorenco/core';
import { useRouter } from 'next/router';

import { TabPanel } from '@/components';

import {
  GetMatchsDocument,
  TeamRankingQueryDocument,
  GetClubQueryDocument,
} from './_internal/Widget.query.generated';

type TeamWidgetProps = {
  teams?: Types.Teams_Team_Detail[];
  settings?: Omit<Types.Widgets_Widget_Team, '__typename'>;
  show_header?: boolean;
};

export const TeamWidget = ({
  teams,
  settings = {
    show_ranking: true,
    show_events: true,
    show_name_in_club: true,
    nb_events_to_show: 10,
    display_priority: 'results',
  },
  show_header = true,
}: TeamWidgetProps) => {
  const router = useRouter();
  const [selectedTeamTab, setSelectedTeamTab] = useState(0);
  const [userEventsTab, setUserEventsTab] = useState<number | null>(null);
  const nbEventsToShow =
    typeof settings?.nb_events_to_show === 'number'
      ? settings.nb_events_to_show
      : 10;

  const eventsTab =
    userEventsTab ?? (settings?.display_priority === 'calendar' ? 1 : 0);

  const getClub = useQuery(GetClubQueryDocument, {
    variables: {
      clubId: teams?.[0]?.club_id as number,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !teams?.[0]?.club_id,
    context: {
      role: 'anonymous',
    },
  });

  const club = getClub.data?.clubs_club_by_pk;
  const selectedTeam = teams?.[selectedTeamTab];

  const getRanking = useQuery(TeamRankingQueryDocument, {
    variables: {
      teamId: selectedTeam?.id,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !selectedTeam?.id || !settings?.show_ranking,
    context: {
      role: 'anonymous',
    },
  });

  const getResults = useQuery(GetMatchsDocument, {
    variables: {
      teamId: selectedTeam?.team_id,
      dateFilter: -1,
      limit: nbEventsToShow,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !selectedTeam?.team_id || !settings?.show_events,
    context: {
      role: 'anonymous',
    },
  });

  const getNextMatchs = useQuery(GetMatchsDocument, {
    variables: {
      teamId: selectedTeam?.team_id,
      dateFilter: 1,
      limit: nbEventsToShow,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !selectedTeam?.team_id || !settings?.show_events,
    context: {
      role: 'anonymous',
    },
  });

  if (!club || !teams?.length) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {teams.length > 1 && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={selectedTeamTab}
            onChange={(_, tabIndex) => {
              setSelectedTeamTab(tabIndex);
            }}
            centered
            variant={'scrollable'}
            scrollButtons="auto"
            allowScrollButtonsMobile
            css={() => css`
              .MuiTabs-scrollButtons.Mui-disabled {
                opacity: 0.3;
              }
            `}
          >
            {teams?.map((t) => (
              <Tab key={t.id} label={t.name_in_club} />
            ))}
          </Tabs>
        </Box>
      )}

      {show_header && (
        <Box
          className="header"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'left',
            alignItems: 'center',
            paddingTop: '1rem',
            paddingLeft: '1rem',
          }}
        >
          <ClubLogo
            logo={selectedTeam?.logo}
            style={{ height: '4rem', width: '4rem', marginRight: '1rem' }}
            width={64}
            height={64}
          />
          <Box>
            <Typography variant="h4" component="p">
              {selectedTeam?.name_in_club}
            </Typography>

            <Chip
              label={[
                club.sport?.name?.toUpperCase(),
                selectedTeam?.name_in_competition,
              ]
                .filter(Boolean)
                .join(' - ')}
              color="primary"
              size="small"
              sx={{ px: 1 }}
            />
          </Box>
        </Box>
      )}

      {settings?.show_ranking && (
        <Paper
          variant="bordered"
          sx={{
            overflow: 'hidden',
            p: 0,
            border: 'none',
            width: '100%',
          }}
        >
          <CompetitionRanking
            rankings={
              getRanking.data?.teams_team_detail_by_pk?.[0]?.team_in_season
                ?.last_competition_with_ranking?.[0]?.rankings
            }
            highlightTeams={selectedTeam?.id ? [selectedTeam?.id] : undefined}
            style={{ width: '100%' }}
            sport={
              getRanking.data?.teams_team_detail_by_pk?.[0]?.club?.[0]?.sport
            }
            slots={{
              row: ({ ranking, ...rest }) => (
                <RouteLink
                  href={`${process.env.NEXT_PUBLIC_WEB_URL}${ranking?.url}`}
                >
                  <ButtonBase component="a" sx={{ display: 'block' }}>
                    <CompetitionRankingRow
                      ranking={ranking}
                      showShortName={settings?.show_name_in_club}
                      clubId={selectedTeam?.club_id}
                      {...rest}
                    />
                  </ButtonBase>
                </RouteLink>
              ),
            }}
          />
        </Paper>
      )}

      {settings?.show_events && (
        <Box sx={{ width: 1, marginBottom: '1.5rem' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={eventsTab}
              onChange={(_, newValue) => {
                setUserEventsTab(newValue);
              }}
              centered
              variant="fullWidth"
            >
              <Tab label="Résultats" />
              <Tab label="Calendrier" />
            </Tabs>
          </Box>
          <TabPanel value={eventsTab} index={0}>
            {getResults.data?.competitions_event_detail_by_team_id?.map(
              (event, idx) => {
                return (
                  <>
                    {idx !== 0 && <Divider key={`divider-${idx}`} />}
                    <RouteLink
                      href={
                        event.url && process.env.NEXT_PUBLIC_WEB_URL + event.url
                      }
                    >
                      <Box
                        component="a"
                        sx={{
                          textDecoration: 'none',
                          color: 'unset',
                        }}
                        target="_blank"
                      >
                        <EventItem
                          showShortName={settings?.show_name_in_club}
                          event={event as Types.Competitions_Event_Detail}
                          clubId={club.id}
                          key={event.id}
                          showDate={true}
                        />
                      </Box>
                    </RouteLink>
                  </>
                );
              }
            )}
          </TabPanel>

          <TabPanel value={eventsTab} index={1}>
            {getNextMatchs.data?.competitions_event_detail_by_team_id?.map(
              (event, idx) => {
                return (
                  <>
                    {idx !== 0 && <Divider key={`divider-${idx}`} />}
                    <RouteLink
                      href={
                        event.url && process.env.NEXT_PUBLIC_WEB_URL + event.url
                      }
                    >
                      <Box
                        component="a"
                        sx={{
                          textDecoration: 'none',
                          color: 'unset',
                        }}
                        target="_blank"
                      >
                        <EventItem
                          showShortName={settings?.show_name_in_club}
                          event={event as Types.Competitions_Event_Detail}
                          clubId={club.id}
                          key={event.id}
                          showDate={true}
                        />
                      </Box>
                    </RouteLink>
                  </>
                );
              }
            )}
          </TabPanel>
        </Box>
      )}
    </Box>
  );
};
